import { fetchJson } from "./http";
import type { AuthUser } from "../contexts/AuthContext";

export type AuthFeaturesResponse = {
  magic_link_enabled: boolean;
  password_change_enabled: boolean;
  email_change_enabled: boolean;
};

export async function getAuthFeatures(signal?: AbortSignal): Promise<AuthFeaturesResponse> {
  return fetchJson<AuthFeaturesResponse>("/auth/features", {
    signal,
    skipAuth: true,
  });
}

export type AuthTokensResponse = {
  access_token: string;
  refresh_token: string;
  user?: AuthUser | null;
};

export async function requestMagicLink(email: string, signal?: AbortSignal): Promise<void> {
  await fetchJson<unknown>(
    "/auth/magic-link/request",
    {
      method: "POST",
      body: { email },
      signal,
      skipAuth: true, // Public endpoint
    },
    undefined,
  );
}

export async function verifyMagicLink(token: string, signal?: AbortSignal): Promise<AuthTokensResponse> {
  return fetchJson<AuthTokensResponse>(
    `/auth/magic-link/verify/${encodeURIComponent(token)}`,
    {
      signal,
      skipAuth: true, // Public endpoint
    },
    undefined,
  );
}

export type InvitationDetailsResponse = {
  tenant: { id: string; slug: string; name: string };
  email: string;
  role: string;
  expires_at: string; // RFC3339
};

export async function getInvitationDetails(
  token: string,
  signal?: AbortSignal,
): Promise<InvitationDetailsResponse> {
  return fetchJson<InvitationDetailsResponse>(
    `/auth/invitation/${encodeURIComponent(token)}`,
    {
      signal,
      skipAuth: true, // Public endpoint
    },
    undefined,
  );
}

export async function acceptInvitation(token: string, signal?: AbortSignal): Promise<AuthTokensResponse> {
  return fetchJson<AuthTokensResponse>(
    `/auth/invitation/${encodeURIComponent(token)}/accept`,
    {
      method: "POST",
      signal,
      skipAuth: true, // Public endpoint
    },
    undefined,
  );
}

export type EmailChangeConfirmResponse = { status: "confirmed_old" | "confirmed_new" | "applied" };

export async function confirmEmailChangeOld(
  token: string,
  signal?: AbortSignal,
): Promise<EmailChangeConfirmResponse> {
  return fetchJson<EmailChangeConfirmResponse>(
    `/auth/email-change/confirm-old/${encodeURIComponent(token)}`,
    { signal, skipAuth: true },
    undefined,
  );
}

export async function confirmEmailChangeNew(
  token: string,
  signal?: AbortSignal,
): Promise<EmailChangeConfirmResponse> {
  return fetchJson<EmailChangeConfirmResponse>(
    `/auth/email-change/confirm-new/${encodeURIComponent(token)}`,
    { signal, skipAuth: true },
    undefined,
  );
}

